// Mode hors ligne : dernière session (/auth/me) et dernier fil lu, mémorisés sur l'appareil.
// L'app reste consultable sans réseau (bandeau discret) au lieu d'un écran plein « Reconnexion ».
// Lot 713 — stories et notifications gardées aussi (garde-fou « net_resilience »), même durée de vie (7 jours).
// Lot 718 — conversations gardées aussi (Messages) ; interrupteur unique : garde-fou « instant_boot » coupé (à distance,
// repli ou mode sécurisé) → les caches de CONTENU (fil, stories, notifications, conversations) sont vidés tout de suite
// et ne sont plus ni écrits ni lus. La session mémorisée (yalola.me) n'en dépend pas (mode reconnexion).
import { guardOn, subscribeGuards } from "@/lib/guards";

const USER_KEY = "yalola.me";
const FEED_KEY = "yalola.feed.";
const STORIES_KEY = "yalola.stories.";
const NOTIFS_KEY = "yalola.notifs.";
const CONVOS_KEY = "yalola.convos.";
const PROFILE_KEY = "yalola.profile.";
const MYPOSTS_KEY = "yalola.myposts.";
const CONTENT_PREFIXES = [FEED_KEY, STORIES_KEY, NOTIFS_KEY, CONVOS_KEY, PROFILE_KEY, MYPOSTS_KEY];
const FEED_MAX_AGE = 7 * 24 * 3600 * 1000;

export const contentCacheOn = () => guardOn("instant_boot");
export const purgeContentCache = () => {
  try { Object.keys(localStorage).filter((k) => CONTENT_PREFIXES.some((p) => k.startsWith(p))).forEach((k) => localStorage.removeItem(k)); } catch { /* noop */ }
};
// Garde-fou coupé pendant que l'app tourne (config rafraîchie) ou connu au démarrage → purge.
subscribeGuards(() => { if (!contentCacheOn()) purgeContentCache(); });
if (!contentCacheOn()) purgeContentCache();

export const readCachedUser = () => {
  try { const u = JSON.parse(localStorage.getItem(USER_KEY) || "null"); return u && u.id ? u : null; } catch { return null; }
};
export const writeCachedUser = (user) => {
  try { user?.id ? localStorage.setItem(USER_KEY, JSON.stringify(user)) : localStorage.removeItem(USER_KEY); } catch { /* quota */ }
};

export const readCachedFeed = (uid) => {
  if (!contentCacheOn()) return null;
  try {
    const f = JSON.parse(localStorage.getItem(FEED_KEY + uid) || "null");
    if (!f || !Array.isArray(f.posts) || !f.posts.length) return null;
    return Date.now() - (f.at || 0) < FEED_MAX_AGE ? f : null;
  } catch { return null; }
};
export const writeCachedFeed = (uid, posts, meta = {}) => {
  if (!contentCacheOn()) return;
  try { localStorage.setItem(FEED_KEY + uid, JSON.stringify({ ...meta, posts: posts.slice(0, 20), at: Date.now() })); } catch { /* quota */ }
};

// Liste datée générique (stories : groupes d'auteurs ; notifications : éléments + compteur non lus ; conversations).
const readList = (key, field) => {
  if (!contentCacheOn()) return null;
  try {
    const f = JSON.parse(localStorage.getItem(key) || "null");
    if (!f || !Array.isArray(f[field]) || !f[field].length) return null;
    return Date.now() - (f.at || 0) < FEED_MAX_AGE ? f : null;
  } catch { return null; }
};
const writeList = (key, payload) => {
  if (!contentCacheOn()) return;
  try { localStorage.setItem(key, JSON.stringify({ ...payload, at: Date.now() })); } catch { /* quota */ }
};
export const readCachedStories = (uid) => readList(STORIES_KEY + uid, "groups");
export const writeCachedStories = (uid, groups) => writeList(STORIES_KEY + uid, { groups: (groups || []).slice(0, 25) });
export const readCachedNotifications = (uid, tab = "all") => readList(`${NOTIFS_KEY}${uid}.${tab}`, "items");
export const writeCachedNotifications = (uid, items, unread = 0, tab = "all") => writeList(`${NOTIFS_KEY}${uid}.${tab}`, { items: (items || []).slice(0, 60), unread });
// Lot 718 — boîte de réception (Messages) : 40 conversations max.
export const readCachedConversations = (uid) => readList(CONVOS_KEY + uid, "conversations");
export const writeCachedConversations = (uid, conversations) => writeList(CONVOS_KEY + uid, { conversations: (conversations || []).slice(0, 40) });
// Lot 719 — MON profil (fiche /users/<moi> + 1re page de mes publications) ; les autres profils ne sont pas gardés.
export const readCachedProfile = (uid) => {
  if (!contentCacheOn()) return null;
  try {
    const f = JSON.parse(localStorage.getItem(PROFILE_KEY + uid) || "null");
    return f?.user?.id === uid && Date.now() - (f.at || 0) < FEED_MAX_AGE ? f.user : null;
  } catch { return null; }
};
export const writeCachedProfile = (uid, user) => { if (user?.id === uid) writeList(PROFILE_KEY + uid, { user }); };
export const readCachedProfilePosts = (uid) => readList(MYPOSTS_KEY + uid, "posts")?.posts || null;
export const writeCachedProfilePosts = (uid, posts) => writeList(MYPOSTS_KEY + uid, { posts: (posts || []).slice(0, 15) });

export const clearOfflineCache = () => {
  try { localStorage.removeItem(USER_KEY); } catch { /* noop */ }
  purgeContentCache();
};
